import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaSave, FaTimesCircle, FaBarcode } from 'react-icons/fa';

import { useAuth } from '~/hooks';
import * as Yup from 'yup';

import api from '~/services/api';

import { Input, Select, Checkbox } from '~/components/Form';
import { TableLoading } from '~/components/Table';

import { Container, Header, Controls } from '~/styles/components';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ConfirmWindow from '~/components/ConfirmWindow';
import { confirmAlert } from 'react-confirm-alert';
import { Content, KpiForm } from './styles';

const Form = () => {
  const { company, user, companyUsers } = useAuth();
  const formRef = useRef(null);
  const history = useHistory();

  const loading = false;

  const [kpi, setKpi] = useState({
    company_id: '',
    description: '',
    weight: '',
    type: '',
    deadline_days: '',
    evaluator_id: '',
    generate_task_schedule: false,
    status: true,
  });

  const usersOptions = useMemo(() => {
    if (user && companyUsers) {
      const options = companyUsers
        .filter(userItem => userItem.user_id !== user.id)
        .filter(userItem => userItem.user_id !== -1)
        .filter(userItem => userItem.active !== false)
        .map(userItem => {
          return { value: userItem.user_id, label: userItem.short_name };
        });

      options.sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });

      options.unshift({
        value: user.id,
        label: user.short_name,
      });

      return options;
    }

    return [];
  }, [companyUsers, user]);

  const typesOptions = useMemo(() => {
    return [
      { value: 0, label: 'Padrão' },
      { value: 1, label: 'Consultivo' },
    ];
  }, []);

  const kpiSchema = Yup.object().shape({
    description: Yup.string().required('Descrição é obrigatória'),
    weight: Yup.string().required('Peso é obrigatório'),
    type: Yup.string().required('Tipo é obrigatório'),
    deadline_days: Yup.string().required('Dias para vencimento é obrigatório'),
    evaluator_id: Yup.string().required('Avaliador é obrigatório'),
  });

  const handleSave = useCallback(async () => {
    try {
      const company_id = company.id;

      const formattedKpi = {
        ...kpi,
        company_id,
      };

      await kpiSchema.validate(formattedKpi, { abortEarly: false });

      await api.post('/kpi-registration', formattedKpi, {
        params: {
          company_id,
          user_id: user.id,
        },
      });

      toast.success('Registro KPI salvo com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      history.push('/kpi-registration');
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
          toast.error(err.message, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        });
        formRef.current.setErrors(validationErrors);
      } else {
        toast.error(
          `Falha ao salvar registro KPI: ${JSON.stringify(
            error.response?.data
          ) ||
            error.message ||
            'Erro desconhecido'}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company, kpi, kpiSchema, user, history]);

  const confirmSaveForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleSave}
            onClose={onClose}
            message="Todos os dados serão salvos com esta ação."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleSave]);

  const handleCancel = useCallback(() => {
    history.push('/kpi-registration');
  }, [history]);

  const confirmCalcelForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return <ConfirmWindow onClick={handleCancel} onClose={onClose} />;
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleCancel]);

  const handleChange = (e, name) => {
    const { value } = e.target;

    setKpi(prevKpi => ({
      ...prevKpi,
      [name]: value,
    }));
  };

  const handleChangeSelect = (selectedOption, name) => {
    setKpi(prevKpi => {
      const newKpi = {
        ...prevKpi,
        [name]: selectedOption.value,
      };

      return newKpi;
    });
  };

  const handleChangeCheckbox = (e, name) => {
    const newValue = !!e.target.checked;
    setKpi(prevKpi => ({
      ...prevKpi,
      [name]: newValue,
    }));
  };

  return (
    <Container>
      <Header>
        <div>
          <FaBarcode />
          <h1>KPI - Cadastro</h1>
        </div>

        <Link to="/">
          <FaTimes />
        </Link>
      </Header>
      <Controls>
        <button type="button" onClick={confirmSaveForm}>
          <FaSave size={15} color="#44546a" />
          <span>Salvar</span>
        </button>
        <button type="button" onClick={confirmCalcelForm}>
          <FaTimesCircle />
          <span>Cancelar</span>
        </button>
      </Controls>
      {loading ? (
        <TableLoading />
      ) : (
        <Content className="content">
          <KpiForm ref={formRef} onSubmit={handleSave}>
            <h4>KPI - Cadastro</h4>
            <section>
              <Input
                name="deadline_days"
                className="deadline_days"
                label="Prazo"
                defaultValue={kpi?.deadline_days}
                onChange={e => handleChange(e, 'deadline_days')}
              />
              <Input
                name="weight"
                className="weight"
                label="Peso"
                defaultValue={kpi?.weight}
                onChange={e => handleChange(e, 'weight')}
              />
              <Select
                label="Tipo"
                name="type"
                className="type"
                options={typesOptions}
                placeholder="Selecione um tipo"
                value={typesOptions.find(option => option.value === kpi?.type)}
                onChange={option => handleChangeSelect(option, 'type')}
              />
              <Input
                name="description"
                className="description"
                label="Descrição"
                defaultValue={kpi?.description}
                onChange={e => handleChange(e, 'description')}
              />
            </section>
            <section>
              <Select
                label="Avaliador (Usuário)"
                name="evaluator_id"
                className="evaluator_id"
                options={usersOptions}
                placeholder="Selecione um usuário"
                value={usersOptions.find(
                  option => option.value === kpi?.evaluator_id
                )}
                onChange={option => handleChangeSelect(option, 'evaluator_id')}
              />
              <Checkbox
                id="generate_schedule_task"
                name="generate_schedule_task"
                className="generate_schedule_task"
                label="Gerar tarefa na Agenda"
                checked={kpi?.generate_schedule_task === true}
                onChange={e =>
                  handleChangeCheckbox(e, 'generate_schedule_task')
                }
              />
            </section>
          </KpiForm>
        </Content>
      )}
    </Container>
  );
};

export default Form;
